import { useState, useRef, useEffect } from 'react';
import { GitBranch, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BranchSelectorProps {
  branches: string[];
  currentBranch: string;
  defaultBranch: string;
  onSelect: (branch: string) => void;
}

export function BranchSelector({ branches, currentBranch, defaultBranch, onSelect }: BranchSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isNonDefault = currentBranch !== defaultBranch;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors',
          isNonDefault
            ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300'
            : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300',
        )}
      >
        <GitBranch className="w-3 h-3" />
        <span className="max-w-[120px] truncate">{currentBranch}</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50 py-1 max-h-64 overflow-y-auto">
          {branches.map((branch) => (
            <button
              key={branch}
              onClick={() => { onSelect(branch); setOpen(false); }}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left hover:bg-slate-50 dark:hover:bg-slate-800',
                branch === currentBranch && 'bg-blue-50 dark:bg-blue-900/20',
              )}
            >
              {branch === currentBranch ? (
                <Check className="w-3 h-3 text-blue-600 shrink-0" />
              ) : (
                <span className="w-3" />
              )}
              <span className="truncate">{branch}</span>
              {branch === defaultBranch && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                  default
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
